import React from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
} from '@one-thd/sui-atomic-components';
import { useSweepstakesData } from '../../../../contexts';
import { RenderSweepstakesDrawerLoading, RenderGenericError } from '../SweepstakesUtils';
import { useImageError } from '../../utils/SweepstakesUtils';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';
import '../../styles/sweepstakes-fonts.scss';

const { DRAWER_TITLE: titleFontStyle } = SWEEPSTAKES_CONSTANTS.FONT_STYLES;

// Render components
const RenderLogo = ({ logoUrl, logo }) => {
  if (!logoUrl) return null;

  return logo.hasError ? (
    <img
      src={SWEEPSTAKES_CONSTANTS.URLS.LOGO_FALLBACK}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      data-testid="sweepstakes-drawer-logo-fallback"
    />
  ) : (
    <img
      src={logoUrl}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      loading="lazy"
      onError={logo.handleError}
      data-testid="sweepstakes-drawer-logo"
    />
  );
};

const RenderSweepstakesTitle = ({ sweepstakesTitle, challengeType, logoUrl, logo }) => {
  if (!logoUrl && !sweepstakesTitle) return null;

  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="sui-flex sui-items-center sui-justify-center sui-gap-7
      sui-mb-4 sui-bg-[#000A24] sui--mx-4 sui--mt-4 sui-px-4 sui-py-4"
    >
      <RenderLogo logoUrl={logoUrl} logo={logo} />
      {sweepstakesTitle && (
        <div>
          <span style={titleFontStyle} data-testid="sweepstakes-drawer-title">
            {sweepstakesTitle}
          </span>
          {challengeType && (
            <>
              {' '}
              <span style={titleFontStyle} data-testid="sweepstakes-drawer-subtitle">
                {challengeType}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const SweepstakesDrawer = ({
  isOpen,
  onClose,
  onBack,
  drawerTitle,
  sweepstakesTitle,
  challengeType,
  logoUrl,
  heroImage,
  children,
  footerContent,
  isLoading,
  hasError,
  testId,
}) => {
  const logo = useImageError();
  const { refresh } = useSweepstakesData();

  if (!isOpen) return null;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      DrawerRootProps={{ style: { maxHeight: '100dvh', height: '100dvh' } }}
      data-testid="sweepstakes-drawer"
    >
      <DrawerHeader
        title={<span className="sui-w-full sui-text-center">{drawerTitle}</span>}
        onClose={onClose}
        onBack={onBack}
        data-testid={testId}
      />
      <DrawerBody>
        {isLoading && <RenderSweepstakesDrawerLoading />}
        {!isLoading && hasError && <RenderGenericError refresh={refresh} />}
        {!isLoading && !hasError && (
          <div className="sui-flex sui-flex-col sui-h-full sui-mt-3">
            <RenderSweepstakesTitle
              sweepstakesTitle={sweepstakesTitle}
              challengeType={challengeType}
              logoUrl={logoUrl}
              logo={logo}
            />
            {children}
          </div>
        )}
      </DrawerBody>
      {!isLoading && !hasError && footerContent && (
        <DrawerFooter variant="sticky">
          {footerContent}
        </DrawerFooter>
      )}
    </Drawer>
  );
};

SweepstakesDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  drawerTitle: PropTypes.string,
  sweepstakesTitle: PropTypes.string,
  challengeType: PropTypes.string,
  logoUrl: PropTypes.string,
  heroImage: PropTypes.string,
  children: PropTypes.node,
  footerContent: PropTypes.node,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  testId: PropTypes.string,
};

SweepstakesDrawer.defaultProps = {
  onBack: null,
  drawerTitle: null,
  sweepstakesTitle: null,
  challengeType: null,
  logoUrl: null,
  heroImage: null,
  children: null,
  footerContent: null,
  isLoading: false,
  hasError: false,
  testId: null,
};
